import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

const initialErrors = {
  serviceName: '',
  description: '',
  serviceProviders: '',
  passType: '',
  price: '',
  percentageDiscount: '',
  validityPeriodDays: '',
  termCond: '',
  applicationLimits: '',
  passengerLimits: '',
  isReturnTrip: '',
  erlServiceType: '',
};

const emptyTerm = { title: '', description: '' };

const defaultPassengerRows = [
  { key: 'ADULT', value: '1' },
];

export default function CreatePassModal({
  open,
  onClose,
  onCreate,
  isCreating,
}) {
  const [serviceName, setServiceName] = useState('');
  const [description, setDescription] = useState('');
  const [serviceProviders, setServiceProviders] = useState('ERL');
  const [passType, setPassType] = useState('UNLIMITED');
  const [price, setPrice] = useState('');
  const [percentageDiscount, setPercentageDiscount] = useState('');
  const [validityPeriodDays, setValidityPeriodDays] = useState('');
  const [zoneIdText, setZoneIdText] = useState('');
  const [isReturnTrip, setIsReturnTrip] = useState('FALSE');
  const [erlServiceType, setErlServiceType] = useState('TRANSIT');
  const [applicationLimitTREK, setApplicationLimitTREK] = useState('');
  const [applicationLimitERL, setApplicationLimitERL] = useState('');
  const [passengerRows, setPassengerRows] = useState(defaultPassengerRows);
  const [terms, setTerms] = useState([emptyTerm]);
  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    if (!open) {
      setServiceName('');
      setDescription('');
      setServiceProviders('ERL');
      setPassType('UNLIMITED');
      setPrice('');
      setPercentageDiscount('');
      setValidityPeriodDays('');
      setZoneIdText('');
      setIsReturnTrip('FALSE');
      setErlServiceType('TRANSIT');
      setApplicationLimitTREK('');
      setApplicationLimitERL('');
      setPassengerRows(defaultPassengerRows);
      setTerms([emptyTerm]);
      setErrors(initialErrors);
    }
  }, [open]);

  const handleAddPassengerRow = () => {
    setPassengerRows((prev) => [...prev, { key: '', value: '' }]);
  };

  const handlePassengerRowChange = (index, field, value) => {
    setPassengerRows((prev) => prev.map((row, i) => (i === index
      ? { ...row, [field]: value } : row)));
  };

  const handleRemovePassengerRow = (index) => {
    setPassengerRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleTermChange = (index, field, value) => {
    setTerms((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const handleAddTerm = () => {
    setTerms((prev) => [...prev, emptyTerm]);
  };

  const handleRemoveTerm = (index) => {
    setTerms((prev) => prev.filter((_, i) => i !== index));
  };

  const validateAndBuildPayload = () => {
    const nextErrors = { ...initialErrors };
    let valid = true;

    if (!serviceName || typeof serviceName !== 'string') {
      nextErrors.serviceName = 'Service name is required.';
      valid = false;
    }

    if (!description || typeof description !== 'string') {
      nextErrors.description = 'Description is required.';
      valid = false;
    }

    if (!serviceProviders || (serviceProviders !== 'ERL' && serviceProviders !== 'TREK')) {
      nextErrors.serviceProviders = 'Service provider must be ERL or TREK.';
      valid = false;
    }

    if (!passType || (passType !== 'LIMITED' && passType !== 'UNLIMITED')) {
      nextErrors.passType = 'Pass type must be LIMITED or UNLIMITED.';
      valid = false;
    }

    const numericPrice = Number(price);
    if (!price || Number.isNaN(numericPrice) || numericPrice <= 0) {
      nextErrors.price = 'Price must be a number greater than 0.';
      valid = false;
    }

    const numericDiscount = Number(percentageDiscount);
    if (
      percentageDiscount === ''
      || Number.isNaN(numericDiscount)
      || !Number.isInteger(numericDiscount)
      || numericDiscount < 1
      || numericDiscount > 100
    ) {
      nextErrors.percentageDiscount = 'Discount must be an integer between 1 and 100.';
      valid = false;
    }

    let numericValidity;
    if (validityPeriodDays !== '') {
      numericValidity = Number(validityPeriodDays);
      if (
        Number.isNaN(numericValidity)
        || !Number.isInteger(numericValidity)
        || numericValidity < 1
      ) {
        nextErrors.validityPeriodDays = 'Validity days must be a positive integer.';
        valid = false;
      }
    }

    const cleanedTerms = terms
      .map((t) => ({
        title: (t.title || '').trim(),
        description: (t.description || '').trim(),
      }))
      .filter((t) => t.title && t.description);

    if (!Array.isArray(cleanedTerms) || cleanedTerms.length === 0) {
      nextErrors.termCond = 'At least one term & condition (title and description) is required.';
      valid = false;
    }

    if (!isReturnTrip || (isReturnTrip !== 'TRUE' && isReturnTrip !== 'FALSE')) {
      nextErrors.isReturnTrip = 'Return trip must be TRUE or FALSE.';
      valid = false;
    }

    if (!erlServiceType || (erlServiceType !== 'TRANSIT' && erlServiceType !== 'EXPRESS')) {
      nextErrors.erlServiceType = 'ERL service type must be TRANSIT or EXPRESS.';
      valid = false;
    }

    const cleanedPassengerRows = passengerRows
      .map((row) => ({
        key: (row.key || '').trim(),
        value: (row.value || '').trim(),
      }))
      .filter((row) => row.key && row.value !== '');

    const passengerLimits = {};
    let passengerError = '';
    cleanedPassengerRows.forEach((row) => {
      const num = Number(row.value);
      if (!Number.isInteger(num) || num <= 0) {
        passengerError = 'Each passenger limit must be a positive integer.';
      } else if (passengerLimits[row.key]) {
        passengerError = 'Passenger limit keys must be unique.';
      } else {
        passengerLimits[row.key] = num;
      }
    });

    if (!cleanedPassengerRows.length) {
      passengerError = 'At least one passenger limit is required.';
    }

    if (passengerError) {
      nextErrors.passengerLimits = passengerError;
      valid = false;
    }

    let applicationLimits;
    if (passType === 'LIMITED') {
      const allowed = {};
      const trekVal = applicationLimitTREK.trim();
      const erlVal = applicationLimitERL.trim();

      if (trekVal !== '') {
        const num = Number(trekVal);
        if (!Number.isInteger(num) || num <= 0) {
          nextErrors.applicationLimits = 'TREK limit must be a positive integer.';
          valid = false;
        } else {
          allowed.TREK = num;
        }
      }

      if (erlVal !== '') {
        const num = Number(erlVal);
        if (!Number.isInteger(num) || num <= 0) {
          nextErrors.applicationLimits = 'ERL limit must be a positive integer.';
          valid = false;
        } else {
          allowed.ERL = num;
        }
      }

      if (!Object.keys(allowed).length) {
        nextErrors.applicationLimits = 'When pass type is LIMITED, at least one of TREK or ERL limit is required.';
        valid = false;
      }

      applicationLimits = allowed;
    }

    setErrors(nextErrors);

    if (!valid) {
      return null;
    }

    const zoneIdArray = zoneIdText
      .split(',')
      .map((z) => z.trim())
      .filter((z) => z.length > 0);

    const payload = {
      serviceName: serviceName.trim(),
      description: description.trim(),
      serviceProviders,
      passType,
      price: numericPrice,
      percentageDiscount: numericDiscount,
      termCond: cleanedTerms,
      passengerLimits,
      isReturnTrip,
      erlServiceType,
    };

    if (applicationLimits) {
      payload.applicationLimits = applicationLimits;
    }
    if (numericValidity) {
      payload.validityPeriodDays = numericValidity;
    }
    if (zoneIdArray.length) {
      payload.zoneId = zoneIdArray;
    }

    return payload;
  };

  const handleSubmit = () => {
    const payload = validateAndBuildPayload();
    if (!payload) {
      return;
    }
    onCreate(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Create Digital Pass</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Define the properties of the new digital pass. All fields should match the rules
            enforced by the backend API.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Service Name"
              margin="dense"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              error={Boolean(errors.serviceName)}
              helperText={errors.serviceName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Service Provider"
              margin="dense"
              value={serviceProviders}
              onChange={(e) => setServiceProviders(e.target.value)}
              error={Boolean(errors.serviceProviders)}
              helperText={errors.serviceProviders || 'ERL or TREK'}
            >
              <MenuItem value="ERL">ERL</MenuItem>
              <MenuItem value="TREK">TREK</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description"
              margin="dense"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={Boolean(errors.description)}
              helperText={errors.description}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Pass Type"
              margin="dense"
              value={passType}
              onChange={(e) => setPassType(e.target.value)}
              error={Boolean(errors.passType)}
              helperText={errors.passType}
            >
              <MenuItem value="UNLIMITED">UNLIMITED</MenuItem>
              <MenuItem value="LIMITED">LIMITED</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Price (MYR)"
              type="number"
              margin="dense"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              error={Boolean(errors.price)}
              helperText={errors.price}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Discount (%)"
              type="number"
              margin="dense"
              value={percentageDiscount}
              onChange={(e) => setPercentageDiscount(e.target.value)}
              error={Boolean(errors.percentageDiscount)}
              helperText={errors.percentageDiscount}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Validity Period (days)"
              type="number"
              margin="dense"
              value={validityPeriodDays}
              onChange={(e) => setValidityPeriodDays(e.target.value)}
              error={Boolean(errors.validityPeriodDays)}
              helperText={errors.validityPeriodDays || 'Optional'}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Return Trip"
              margin="dense"
              value={isReturnTrip}
              onChange={(e) => setIsReturnTrip(e.target.value)}
              error={Boolean(errors.isReturnTrip)}
              helperText={errors.isReturnTrip}
            >
              <MenuItem value="FALSE">FALSE</MenuItem>
              <MenuItem value="TRUE">TRUE</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="ERL Service Type"
              margin="dense"
              value={erlServiceType}
              onChange={(e) => setErlServiceType(e.target.value)}
              error={Boolean(errors.erlServiceType)}
              helperText={errors.erlServiceType}
            >
              <MenuItem value="TRANSIT">TRANSIT</MenuItem>
              <MenuItem value="EXPRESS">EXPRESS</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Zone IDs (comma separated)"
              margin="dense"
              value={zoneIdText}
              onChange={(e) => setZoneIdText(e.target.value)}
              helperText="Optional. Example: ZONE_A, ZONE_B"
            />
          </Grid>

          {passType === 'LIMITED' && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>
                Application Limits (required for LIMITED passes)
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                <TextField
                  label="TREK Limit"
                  type="number"
                  value={applicationLimitTREK}
                  onChange={(e) => setApplicationLimitTREK(e.target.value)}
                />
                <TextField
                  label="ERL Limit"
                  type="number"
                  value={applicationLimitERL}
                  onChange={(e) => setApplicationLimitERL(e.target.value)}
                />
              </Stack>
              {errors.applicationLimits && (
                <Typography variant="caption" color="error">
                  {errors.applicationLimits}
                </Typography>
              )}
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Passenger Limits
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Define how many passengers of each type this pass supports (e.g. ADULT, CHILD).
            </Typography>
            <Stack spacing={1}>
              {passengerRows.map((row, index) => (
                <Stack
                  key={row.key || row.value || `passenger-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems="center"
                >
                  <TextField
                    label="Key (e.g. ADULT)"
                    value={row.key}
                    onChange={(e) => handlePassengerRowChange(index, 'key', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Limit"
                    type="number"
                    value={row.value}
                    onChange={(e) => handlePassengerRowChange(index, 'value', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  {passengerRows.length > 1 && (
                    <Button
                      color="error"
                      onClick={() => handleRemovePassengerRow(index)}
                    >
                      Remove
                    </Button>
                  )}
                </Stack>
              ))}
              <Button size="small" onClick={handleAddPassengerRow}>
                Add Passenger Type
              </Button>
            </Stack>
            {errors.passengerLimits && (
              <Typography variant="caption" color="error">
                {errors.passengerLimits}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Terms &amp; Conditions
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {terms.map((term, index) => (
                <Box
                  key={term.title || term.description || `term-${index}`}
                  sx={{
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    p: 2,
                  }}
                >
                  <Stack spacing={1}>
                    <TextField
                      label="Title"
                      fullWidth
                      value={term.title}
                      onChange={(e) => handleTermChange(index, 'title', e.target.value)}
                    />
                    <TextField
                      label="Description"
                      fullWidth
                      multiline
                      minRows={2}
                      value={term.description}
                      onChange={(e) => handleTermChange(index, 'description', e.target.value)}
                    />
                    {terms.length > 1 && (
                      <Button
                        color="error"
                        size="small"
                        onClick={() => handleRemoveTerm(index)}
                      >
                        Remove Term
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
              <Button size="small" onClick={handleAddTerm}>
                Add Term
              </Button>
            </Stack>
            {errors.termCond && (
              <Typography variant="caption" color="error">
                {errors.termCond}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isCreating}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isCreating}>
          {isCreating ? 'Creating…' : 'Create Pass'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
